import { CalendarIcon, HeartIcon } from "lucide-react";
import { useState } from "react";
import { useGetDonationsDailyHonoreesQuery } from "../../../state/domainRouterApi";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import { ModuleSectionNav } from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { DONATIONS_SECTION_ITEMS } from "./sectionNav";

const AMBER_PRIMARY = "#d97706";
const AMBER_BG = "#fffbeb";
const AMBER_BORDER = "#fde68a";
const TEXT = "var(--iron-grey)";

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((index) => (
        <div
          key={index}
          className="h-20 animate-pulse rounded-xl bg-amber-50"
        />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

function formatDisplayDate(dateString: string): string {
  try {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

type HonoreeEntry = {
  honoreeName?: string;
  dedicationType?: string;
  donorName?: string;
};

export function TeacherDailyHonoreesPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);

  const { data, isLoading, isError, error } = useGetDonationsDailyHonoreesQuery(
    {
      date: selectedDate,
    },
  );

  const honorees: HonoreeEntry[] = (() => {
    if (!data) return [];
    const candidate = data as Record<string, unknown>;
    if (Array.isArray(candidate.items)) {
      return (candidate.items as Array<Record<string, unknown>>).map(
        (item) => ({
          honoreeName:
            typeof item.honoreeName === "string" ? item.honoreeName : "Unknown",
          dedicationType:
            item.dedicationType === "MEMORY" ? "In Memory Of" : "In Honor Of",
          donorName: Boolean(item.isAnonymous)
            ? "Anonymous"
            : typeof item.publicDisplayName === "string" &&
                item.publicDisplayName
              ? item.publicDisplayName
              : "A YST Family",
        }),
      );
    }
    return [];
  })();

  const hebrewDate =
    typeof (data as Record<string, unknown> | undefined)?.hebrewDateLabel ===
    "string"
      ? String((data as Record<string, unknown>).hebrewDateLabel)
      : null;

  return (
    <main
      data-testid="school-donations-daily-honorees"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="min-h-screen"
      style={{ backgroundColor: AMBER_BG }}
    >
      <YstRouteTransition className="mx-auto max-w-[1120px] space-y-6">
        <ModuleSectionNav
          items={DONATIONS_SECTION_ITEMS}
          activeId="daily-honorees"
          ariaLabel="Donations sections"
          desktopClassName="source-faithful-tabs"
        />

        <div className="mx-auto max-w-[800px] space-y-6">
          <div className="text-center">
            <div className="mb-3 inline-flex items-center gap-2">
              <HeartIcon
                className="h-5 w-5"
                style={{ color: AMBER_PRIMARY }}
                aria-hidden="true"
              />
              <p
                className="yst-surface-eyebrow"
                style={{ color: AMBER_PRIMARY }}
              >
                Daily Honorees
              </p>
            </div>
            <h2 className="yst-surface-title" style={{ color: TEXT }}>
              {formatDisplayDate(selectedDate)}
            </h2>
            {hebrewDate ? (
              <p className="mt-1 text-sm" style={{ color: `${TEXT}88` }}>
                {hebrewDate}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-center gap-3">
            <CalendarIcon
              className="h-4 w-4"
              style={{ color: AMBER_PRIMARY }}
              aria-hidden="true"
            />
            <label
              htmlFor="honoree-date"
              className="text-sm font-medium"
              style={{ color: TEXT }}
            >
              Select date:
            </label>
            <TailwindDateField
              id="honoree-date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="yst-control-input px-3 py-2 text-sm"
              style={{
                borderColor: AMBER_BORDER,
                color: TEXT,
                backgroundColor: "#fff",
              }}
            />
          </div>

          {isLoading ? (
            <LoadingSkeleton />
          ) : isError ? (
            <ErrorBanner
              message={
                (error && typeof error === "object" && "message" in error
                  ? String((error as { message?: unknown }).message)
                  : null) ??
                "Honoree data is not yet available. The donations backend has not been deployed."
              }
            />
          ) : honorees.length === 0 ? (
            <div
              className="rounded-xl border py-16 text-center text-sm font-medium shadow-sm"
              style={{
                borderColor: AMBER_BORDER,
                backgroundColor: "#fff",
                color: TEXT,
              }}
            >
              No approved dedications for this date.
            </div>
          ) : (
            <div className="space-y-4">
              {honorees.map((honoree) => (
                <article
                  key={`${honoree.honoreeName}-${honoree.donorName}`}
                  className="yst-surface-panel yst-motion-color p-5"
                  style={{ borderColor: AMBER_BORDER }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${AMBER_PRIMARY}18` }}
                    >
                      <HeartIcon
                        className="h-5 w-5"
                        style={{ color: AMBER_PRIMARY }}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2
                        className="text-lg font-semibold"
                        style={{ color: TEXT }}
                      >
                        {honoree.honoreeName ?? "Unknown"}
                      </h2>
                      <p
                        className="mt-0.5 text-sm"
                        style={{ color: `${TEXT}88` }}
                      >
                        {honoree.dedicationType ?? "Dedication"} &middot;
                        Sponsored by {honoree.donorName ?? "Anonymous"}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </YstRouteTransition>
    </main>
  );
}
